    import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from './ui/button';
import { getQueryFn, apiRequest } from '../lib/queryClient';
import { useToast } from '../hooks/use-toast';
import { InviteMemberModal } from './invite-member-modal';

interface CompanyMember {
  id: number;
  user_id: number;
  company_id: number;
  role: 'COMPANY_ADMIN' | 'COMPANY_MEMBER';
  is_active: boolean;
  created_at: string;
  user_email: string;
  user_full_name: string | null;
  invited_by_email: string | null;
  invited_by_name: string | null;
}

interface TeamMembersTabProps {
  companyId: number;
  companyName: string;
  isAdmin: boolean;
}

export function TeamMembersTab({ companyId, companyName, isAdmin }: TeamMembersTabProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [memberToUpdate, setMemberToUpdate] = useState<CompanyMember | null>(null);

  const { data: members, isLoading } = useQuery<CompanyMember[]>({
    queryKey: [`/api/companies/${companyId}/members`],
    queryFn: getQueryFn(),
  });

  const removeMemberMutation = useMutation({
    mutationFn: async (memberId: number) => {
      const response = await apiRequest('DELETE', `/api/companies/${companyId}/members/${memberId}`);
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to remove member');
      }
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Member removed successfully',
      });
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/members`] });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const updateMemberMutation = useMutation({
    mutationFn: async ({ memberId, role }: { memberId: number; role: 'COMPANY_ADMIN' | 'COMPANY_MEMBER' }) => {
      const response = await apiRequest('PATCH', `/api/companies/${companyId}/members/${memberId}`, {
        role
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update member');
      }
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: 'Success',
        description: 'Member role updated successfully',
      });
      queryClient.invalidateQueries({ queryKey: [`/api/companies/${companyId}/members`] });
      setMemberToUpdate(null);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  const handleRemoveMember = (member: CompanyMember) => {
    if (window.confirm(`Are you sure you want to remove ${member.user_full_name || member.user_email} from the team?`)) {
      removeMemberMutation.mutate(member.id);
    }
  };

  const handleChangeRole = (member: CompanyMember) => {
    const newRole = member.role === 'COMPANY_ADMIN' ? 'COMPANY_MEMBER' : 'COMPANY_ADMIN';
    updateMemberMutation.mutate({ memberId: member.id, role: newRole });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold">Team Members</h3>
          <p className="text-sm text-gray-600">
            Manage who has access to {companyName}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowInviteModal(true)}>
            <i data-feather="user-plus" className="w-4 h-4 mr-2"></i>
            Invite Member
          </Button>
        )}
      </div>

      {/* Members List */}
      {members && members.length > 0 ? (
        <div className="space-y-4">
          {members.map((member) => (
            <div
              key={member.id}
              className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <i data-feather="user" className="w-5 h-5 text-blue-600"></i>
                    </div>
                    <div>
                      <div className="font-medium">
                        {member.user_full_name || 'No name'}
                      </div>
                      <div className="text-sm text-gray-600">
                        {member.user_email}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      member.role === 'COMPANY_ADMIN' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {member.role === 'COMPANY_ADMIN' ? 'Admin' : 'Member'}
                    </span>
                    <span>Joined {formatDate(member.created_at)}</span>
                    {member.invited_by_name && (
                      <span>Invited by {member.invited_by_name}</span>
                    )}
                  </div>
                </div>

                {isAdmin && (
                  <div className="flex items-center space-x-2">
                    {member.role === 'COMPANY_ADMIN' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangeRole(member)}
                        disabled={updateMemberMutation.isPending}
                      >
                        <i data-feather="user-minus" className="w-4 h-4 mr-1"></i>
                        Demote
                      </Button>
                    )}
                    {member.role === 'COMPANY_MEMBER' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleChangeRole(member)}
                        disabled={updateMemberMutation.isPending}
                      >
                        <i data-feather="user-plus" className="w-4 h-4 mr-1"></i>
                        Promote
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRemoveMember(member)}
                      disabled={removeMemberMutation.isPending}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <i data-feather="trash-2" className="w-4 h-4 mr-1"></i>
                      Remove
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <i data-feather="users" className="w-12 h-12 text-gray-400 mx-auto mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No team members yet</h3>
          <p className="text-gray-600 mb-4">
            Start building your team by inviting members to collaborate.
          </p>
          {isAdmin && (
            <Button onClick={() => setShowInviteModal(true)}>
              <i data-feather="user-plus" className="w-4 h-4 mr-2"></i>
              Invite First Member
            </Button>
          )}
        </div>
      )}

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        companyId={companyId}
        companyName={companyName}
      />
    </div>
  );
}



